import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { KeyRound, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/common/states';
import { Brand } from '@/components/layout/Brand';
import { toast } from '@/components/ui/sonner';
import { useAuthStore } from '@/lib/auth/store';
import { accountApi } from '@/lib/api/endpoints';
import { AdminApiError } from '@/lib/api/client';

const schema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: z
      .string()
      .min(8, 'Use at least 8 characters')
      .max(72, 'Keep it under 72 characters'),
    confirm: z.string().min(1, 'Confirm the new password'),
  })
  .superRefine((v, ctx) => {
    if (v.newPassword !== v.confirm) {
      ctx.addIssue({ code: 'custom', path: ['confirm'], message: 'Passwords do not match' });
    }
    if (v.newPassword && v.newPassword === v.currentPassword) {
      ctx.addIssue({
        code: 'custom',
        path: ['newPassword'],
        message: 'Choose a password different from the current one',
      });
    }
  });
type FormValues = z.infer<typeof schema>;

/**
 * Mandatory password rotation. Shown (in place of the console) whenever the
 * signed-in admin is flagged mustChangePassword — e.g. the seeded default
 * account. The backend blocks every non-self-service route until this succeeds.
 */
export function ForcePasswordChange() {
  const markPasswordChanged = useAuthStore((s) => s.markPasswordChanged);
  const logout = useAuthStore((s) => s.logout);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      await accountApi.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      markPasswordChanged();
      toast.success('Password updated. Welcome in.');
    } catch (err) {
      setServerError(
        err instanceof AdminApiError
          ? err.message
          : 'Could not update the password. Please try again.',
      );
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm space-y-6">
        <Brand />
        <div className="flex items-start gap-3 rounded-md border border-warning/40 bg-warning/10 px-3 py-2.5">
          <KeyRound className="mt-0.5 h-4 w-4 text-warning-foreground" />
          <div>
            <p className="text-sm font-medium">Set a new password to continue</p>
            <p className="text-xs text-muted-foreground">
              Your account uses a default or reset password. Choose a new one before using the
              console.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="currentPassword">Current password</Label>
            <Input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              {...register('currentPassword')}
            />
            {errors.currentPassword ? (
              <p className="text-xs text-destructive">{errors.currentPassword.message}</p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="newPassword">New password</Label>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              {...register('newPassword')}
            />
            {errors.newPassword ? (
              <p className="text-xs text-destructive">{errors.newPassword.message}</p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              {...register('confirm')}
            />
            {errors.confirm ? (
              <p className="text-xs text-destructive">{errors.confirm.message}</p>
            ) : null}
          </div>

          {serverError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {serverError}
            </div>
          ) : null}

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? <Spinner /> : null}
            Update password
          </Button>
        </form>

        <button
          type="button"
          onClick={logout}
          className="mx-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </div>
  );
}

export default ForcePasswordChange;
